import React, {useEffect, useState} from 'react';

import './Patients.detail.scss';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import gqlSchemaLibrary from '../../services/graphql.schemas';
import { PatientAvatarUpload } from '../../visuals/Patient.avatar.uploader';
import moment from 'moment';
import KitEntry from "./Patient.detail.kit";

import SpinnerLoader from "../../visuals/SpinnerLoader";

import ItemMenu from '../../forms/menu/MenuComponent';
import {useNavigate, useParams} from "react-router-dom";
import ModalPatientExtent from "../../visuals/ModalPatientExtent";
import Select from "../../forms/inputs/Select";
import TooltipWrapper from "../../visuals/tooltip/Tooltip.wrapper";
import {getAddressFormat} from "../../utils/address/addressFormat";
import EnrollPatientDialog from "../Patient.enroll";
import {getVitalData, VITAL_TEMP, VITAL_W, VITAL_HR, VITAL_RR, VITAL_BP} from "../../utils/vital";
import {getDateFormat} from "../../utils/date";
import {useTranslation} from "react-i18next";
import CookieSetter from "../../services/cookieSetter";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";
import {clearGqlTypename} from "../../utils/clearTypename";

function EventEntry({ currentOrg, event }) {
    const {t} = useTranslation(['participants'])

    const getAction = () => {
        if (event.action === 'Registered') {
            return t('not-enrolled');
        }
        if (event.action === 'Unpause') {
            return t('unpaused');
        }
        return event.action;
    }

    return (
        <div className="eventEntry">
            <div className="_action">
                <span>{getAction()}</span>
                <TooltipWrapper label={event.actor} value={''}>
                    <i className="material-icons">info</i>
                </TooltipWrapper>
            </div>
            <p className="_date">
                {getDateFormat(currentOrg, event.date, 'long')}
            </p>
            <p className="_description">{event.description}</p>
            <p className="_note">{event.note}</p>
        </div>
    )
}

const getPhone = (phone) => {
    if (!phone || !phone.number) {
        return '';
    }
    if (phone.number.search(/\+/) !== -1) {
            return phone.number;
        }
    return '+' + phone.code + ' ' + phone.number;
}

function ContactEntry({ contact }) {
    const {t} = useTranslation(['users'])
    const getPhoneLabel = () => {
        if (contact.phones && contact.phones.length !== 0) {
            return getPhone(contact.phones[0]);
        }
        return "";
    }
    const getRelationshipLabel = () => {
        if (contact.relationship === 'orderingProvider') {
            return "Ordering Provider";
        }
        return contact.relationship;
    }

    return (
        <div className="contactEntry">
            <div className="row">
                <div>{contact.firstname} {contact.lastname}</div>
                <div className="_relation">{getRelationshipLabel()}</div>
            </div>
            <div className="row">
                <div className="_phone">{getPhoneLabel()}</div>
                <div className="labelType">
                    {
                        contact.contactType === "EmergencyOnly" ? <span>{t('emergency-contact')}</span> : <span>{t('secondary')}</span>
                    }
                </div>
            </div>
        </div>
    )
}

function DemographicEntry({ label, type, value, remark, capitalize }) {

    const getValue = () => {
        if (type && type === 'array') {
            return (
                value.map((v, i) => (
                    <p key={'value-'+i}>
                        {v}
                    </p>
                ))
            )
        }
        return (
            <p>{value}</p>
        );
    }

    return (
        <div className="entry">
            <p className="label">{label}</p>
            <div className={`${remark ? 'remark' : ''} ${capitalize ? 'capitalize' : ''}`} >{getValue()}</div>
        </div>
    )
}


function PatientDetail({ currentOrg, currentUser, match }) {
    let {patientid} = useParams();
    const {t} = useTranslation(['participants','users','common'])
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [program, setProgram] = useState(null);
    const [alertNotifications, setAlertNotifications] = useState([]);
    const [kits, setKits] = useState([]);
    const [extendEnrollmentModal, setExtendEnrollmentModal] = useState(false);
    const [state, setState] = useState(1);
    const [onAction, setOnAction] = useState(false);
    const [values, setValues] = useState([]);
    const [openEnrollPatient, setOpenEnrollPatient] = useState(false);
    const [members, setMembers] = useState([]);

    const [unPauseEnrollment] = useMutation(gqlSchemaLibrary.patients.mutations.unPauseEnrollment, {
        onCompleted: ({ unPauseEnrollment }) => {
            console.log('unPauseEnrollment: ', unPauseEnrollment);
            setOnAction(false);
            setPatient(data => ({ ...data, ...unPauseEnrollment }))
        },
        onError: (error) => {
            setOnAction(false);
        }
    });

    const programOptions = [
        {
            label: t('extend-enrollment'),
            value: 1,
            disabled: !!patient && !patient.expectedUnenrollment
        },
        {
            label: !!patient && patient.isPaused ? `${t('unpause')} ${t('enrollment')}` : t('pause-enrollment'),
            value: 2,
            disabled: !!patient && patient.isDisenrolled
        },
        {
            label: t('disenroll'),
            value: 3
        }
    ]

    const getHighestValue = (data, keys) => {
        if (data && data.attributes) {
            let highKey = "";
            let value = 0;
            for(const k of keys) {
                const v = data.attributes[k];
                if (v > value) {
                    value = v;
                    highKey = k;
                }
            }
            if (highKey) {
                highKey = highKey.charAt(0).toUpperCase() + highKey.slice(1);
            }
            return highKey;
        }
        return '';
    }

    const getSleepHighestValue = (data) => {
        if (data && data.attributes) {
            let highKey = '';
            if (data.attributes.hasOwnProperty('asleep')) {
                highKey = data.attributes.asleep;
            }
            return highKey;
        }
        return '';
    }

    const setVitalLastData = (type, value, label) => {
        const vital = program.vitals.find((v) => v.value === type);
        let _values = Object.assign([], values);
        let _v = values.find((v) => v.key === type);
        if (_v) {
            _v.value = value;
        } else {
            _values.push({
                key: type,
                label: label? label : type,
                value: value,
                order: vital.order
            });
        }
        setValues(_values);
    }

    const [getLastSpo2] = useLazyQuery(gqlSchemaLibrary.data.spo2.LAST_SPO2, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastSPO2) {
                value = (data.getLastSPO2.data).toFixed(1);
            }
            setVitalLastData('SPO2', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastHR] = useLazyQuery(gqlSchemaLibrary.data.heartRate.LAST_HEARTRATE, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastHeartRate) {
                value = getVitalData(currentOrg, VITAL_HR, data.getLastHeartRate.data);
            }
            setVitalLastData('HR', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastTemp] = useLazyQuery(gqlSchemaLibrary.data.temperature.LAST_TEMPERATURE, {
        onCompleted: (data) => {
            let value = '--';
            let label = t('common:vitals.skin-temp');
            if (data.getLastTemperature) {
                let skin = data.getLastTemperature.skin;
                skin = skin && skin.length !== 0? skin[0] : null;
                if (skin) {
                    let org = currentOrg? Object.assign({}, currentOrg) : {};
                    const defaultUserTempUnit = CookieSetter.getCookieValue('temp_unit');
                    if (defaultUserTempUnit) {
                        org.temperatureUnit = defaultUserTempUnit;
                    }
                    value = getVitalData(org, VITAL_TEMP, skin.data);
                }
            }
            setVitalLastData('Temp', value, label);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastRR] = useLazyQuery(gqlSchemaLibrary.data.respiratoryRate.LAST_RESP_RATE, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastRespRate) {
                value = getVitalData(currentOrg, VITAL_RR, data.getLastRespRate.data);
            }
            setVitalLastData('RR', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastW] = useLazyQuery(gqlSchemaLibrary.data.weight.LAST_WEIGHT, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastWeight) {
                value = getVitalData(currentOrg, VITAL_W, data.getLastWeight.weight);
            }
            setVitalLastData('W', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastBP] = useLazyQuery(gqlSchemaLibrary.data.bloodPressure.LAST_BP, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastBloodPressure) {
                const systolic = getVitalData(currentOrg, VITAL_BP, data.getLastBloodPressure.systolic);
                const diastolic = getVitalData(currentOrg, VITAL_BP, data.getLastBloodPressure.diastolic);
                value = systolic+'/'+diastolic;
            }
            setVitalLastData('BP', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastActivity] = useLazyQuery(gqlSchemaLibrary.data.activity.LAST_ACTIVITY, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastActivityLevel) {
                value = getHighestValue(data.getLastActivityLevel, ["low", "medium", "high"]);
            }
            setVitalLastData('Activity', value);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getLastSleep] = useLazyQuery(gqlSchemaLibrary.data.sleep.LAST_SLEEP, {
        onCompleted: (data) => {
            let value = '--';
            if (data.getLastSleep) {
                value = getSleepHighestValue(data.getLastSleep);
            }
            setVitalLastData('Sleep', value);
        },
        fetchPolicy: 'cache-and-network'
    });


    const [fetchPersonByProgram] = useLazyQuery(gqlSchemaLibrary.programs.query.getProgramById, {
        onCompleted: (getProgramById) => {
            console.log('Program : ', getProgramById);
            if (getProgramById.getProgramById) {
                setProgram(getProgramById.getProgramById);

                const vitals = getProgramById.getProgramById.vitals;
                for (let i = 0; i < vitals.length; i++) {
                    const v = vitals[i];
                    if (v.value === 'SPO2') {
                        getLastSpo2({
                            variables: {
                                patientId: patientid
                            }
                        })
                    }
                    if (v.value === 'HR') {
                        getLastHR({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                    if (v.value === 'Temp') {
                        getLastTemp({
                            variables: {
                                patientId: patientid
                            }
                        })
                    }
                    if (v.value === 'RR') {
                        getLastRR({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                    if (v.value === 'W') {
                        getLastW({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                    if (v.value === 'BP') {
                        getLastBP({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                    if (v.value === 'Activity') {
                        getLastActivity({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                    if (v.value === 'Sleep') {
                        getLastSleep({
                            variables: {
                                personId: patientid
                            }
                        })
                    }
                }
            } else {
                setProgram(null);
            }
        },
        fetchPolicy: 'cache-and-network'
    });

    const [fetchPerson] = useLazyQuery(gqlSchemaLibrary.patients.queries.getPerson, {
        onCompleted: (data) => {
            if (!data.getPerson || currentUser.selectedOrgId !== data.getPerson.organizationId) {
                navigate(-1);
            }
            console.log('person to edit: ', currentUser);
            // Update current inputs to edit clinician, disable inputs where necessary
            const p = { ...data.getPerson };
            setPatient(p);
            if (p.programId) {
                fetchPersonByProgram({
                    variables: {
                        id: p.programId
                    }
                });
            } else {
                setProgram(null);
            }
            if (p.programId) {
                getNotificationsAlerts({
                    variables: {
                        patientId: patientid,
                        programId: p.programId
                    }
                });
            }
            getUserAndTeamsByPatient({
                variables: {
                    id: patientid
                }
            });
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getUserAndTeamsByPatient] = useLazyQuery(gqlSchemaLibrary.patients.getUserAndTeamsByPatient, {
        onCompleted: (data) => {
            const _members = [];
            for (const m of data.getUserAndTeamsByPatient.clinicians) {
                const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
                _members.push({
                    _id: m._id,
                    name: name,
                    role: m.role,
                    email: m.user.email,
                    type: "User"
                });
            }
            for (const t of data.getUserAndTeamsByPatient.teams) {
                _members.push({
                    _id: t._id,
                    name: t.name,
                    role: "",
                    email: "",
                    type: "Team"
                });
            }
            setMembers(_members);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [getNotificationsAlerts] = useLazyQuery(gqlSchemaLibrary.notifications.alerts.NOTIFICATIONS_BY_PATIENT, {
        onCompleted: (data) => {
            if (data.getAllNotificationsByPatient) {
                let alerts = data.getAllNotificationsByPatient.filter((a) => !a.acknowledged);
                setAlertNotifications(alerts);
            }
        },
        fetchPolicy: 'cache-and-network'
    });

    useQuery(gqlSchemaLibrary.devices.peripherial.PERIPHERIAL_STATUS_BY_PATIENT, {
        variables: {
            patientId: patientid
        },
        onCompleted: ({ getPeripherialsStatusByPatient }) => {
            let _kits = {};
            getPeripherialsStatusByPatient.forEach((d) => {
                const peripherial = d.device;
                if (peripherial) {
                    const isKit = !!peripherial.kitId;
                    let name = "";
                    let key = peripherial.kitId;
                    if (!isKit) {
                        name = peripherial.displayName;
                        key = peripherial.deviceId;
                    }
                    const lastUpdate = !!d.status && d.status.length? d.status[d.status.length-1].dateCreated : d.assignedAt;
                    if (key in _kits) {
                        if (lastUpdate > _kits[key]["lastUpdate"]) {
                            _kits[key]["lastUpdate"] = lastUpdate;
                        }
                        _kits[key]["devices"].push({
                            "deviceId": peripherial.deviceId,
                            "name": peripherial.displayName
                        });
                    } else {
                        _kits[key] = {
                            "kitId": key,
                            "isKit": isKit,
                            "name": name,
                            "dateCreated": peripherial.dateCreated,
                            "assignedAt": peripherial.assignedAt,
                            "lastUpdate": lastUpdate,
                            "devices": [
                                {
                                    "deviceId": peripherial.deviceId,
                                    "name": peripherial.displayName
                                }
                            ],
                            "status": d.status
                        };
                    }
                }
            });
            const sortKits = Object.values(_kits).sort((a, b) => {
                return b.lastUpdate - a.lastUpdate;
            });
            setKits(sortKits);
        },
        fetchPolicy: 'cache-and-network'
    })

    useEffect(() => {
        if (patientid) {
            fetchPerson({
                variables: {
                    personId: patientid
                }
            });
        }
    }, [match]);

    const goToEditPatient = () => {
        AuditTrail.sendLog(PAGES.PARTICIPANTS_DETAIL, {tag: 'button', event: 'onClick', action: 'click option edit patient', patient_id: patient._id});
        navigate(`/participants/edit/${patient._id}`)
    }

    const onStatusChange = (person) => {
        if (!!person) {
            fetchPerson({
                variables: {
                    personId: person._id
                }
            });
        }
        setExtendEnrollmentModal(false);
    }

    const onStatusChangeRequested = (state) => {
        setState(state);
        if (state === 2 && patient.isPaused) {
            // This is an unpause event
            if (onAction) {
                return;
            }
            setOnAction(true);
            unPauseEnrollment({
                variables: {
                    personId: patient._id
                }
            })
        } else {
            setExtendEnrollmentModal(true);
        }
    }

    const joinAddress = (address) => {
        if (!address || (!!address && !address[0])) {
            return [];
        }
        return getAddressFormat(address[0]);
    }

    const statusTags = () => {
        if (patient.isPaused) {
            return (
                <div className={`tag _paused`}>
                    {t('paused').toUpperCase()}
                </div>
            )
        }
        else if (patient.isDisenrolled) {
            return (
                <div className={`tag _disenrolled`}>
                    {
                        patient.timeline.length <= 1? t('not-enrolled').toUpperCase() : t('disenrolled').toUpperCase()
                    }
                </div>
            )
        }
        else if (!patient.programId) {
            return (
                <div className={`tag _registered`}>
                    {t('common:registered').toUpperCase()}
                </div>
            )
        }
        else if (patient.programId) {
            return (
                <div className={`tag _enrolled`}>
                    {t('enrolled').toUpperCase()}
                </div>
            )
        }
        else {
            return ''
        }
    }

    const enrollStatusLabel = () => {
        if (patient.isPaused && !patient.isDisenrolled) {
            return (<label className="_text _paused">{t('paused')}</label>)
        } if (patient.isDisenrolled) {
            return (<label className="_text _disenrolled">{t('disenrolled')}</label>)
        } else if (!patient.isDisenrolled && !patient.programId) {
            return (<label className="_text _registered">{t('common:registered')}</label>)
        } else if (patient.programId) {
            return (<label className="_text _enrolled">{t('enrolled')}</label>)
        } else {
            return (<label className="_text _registered">{t('common:registered')}</label>)
        }
    }

    const onEnroll = () => {
        AuditTrail.sendLog(PAGES.PARTICIPANTS_DETAIL, {tag: 'button', event: 'onClick', action: 'click enroll patient', patient_id: patient._id});
        setOpenEnrollPatient(true);
    }

    const onCloseModalEnroll = (patientId) => {
        if (patientId) {
            fetchPerson({
                variables: {
                    personId: patientId
                }
            });
        }
        setOpenEnrollPatient(false);
    }

    if (!patient) {
        return <SpinnerLoader />
    }

    const getTimeline = () => {
        const timeline = clearGqlTypename(patient.timeline);
        return timeline.reverse() || [];
    }

    const dobMoment = patient.dateOfBirth? moment(patient.dateOfBirth) : 0;
    return (
        <div id="PatientDetail">
            <div className='userBanner'>
                <div className="imageUploadHolder">
                    <PatientAvatarUpload bio={patient.user.bio} hideIfNone={true} />
                </div>
                <div className="bio">
                    <h1>{patient.user.bio.lastname}, {patient.user.bio.firstname}</h1>
                    <div className="bioDetails">
                        <p>
                            {dobMoment? moment().diff(dobMoment, 'years') : '--'}yo
                        </p>
                        <p>
                            {patient.user.bio.gender}
                        </p>
                        <p>
                            ({getDateFormat(currentOrg, patient.dateOfBirth, 'short')})
                        </p>
                    </div>
                    {statusTags()}
                </div>
                <div className="valueLabel">
                    <div className="values">
                        {
                            values.sort((a, b) => a.order - b.order).map((v, i) => (
                                <p key={'label-'+i}>
                                    &nbsp;{v.label} <span>{v.value}</span>
                                </p>
                            ))
                        }
                    </div>
                    <p className="label">
                        {t('latest-readings')}
                    </p>
                </div>
                <div className="valueLabel">
                    <span>{alertNotifications.length}</span>
                    <p className="label">{alertNotifications.length === 1? t('alert') : t('alerts')}</p>
                </div>
                <div className="valueLabel">
                    <p className="label">
                        {patient.programId ? 1 : 0} {t('users:program')}
                    </p>
                </div>
            </div>
            <div className="userDetails">
                <div className="demographics card">
                    <div className="header">
                        <p className="title">
                            {t('users:demographics')}
                        </p>
                        {
                            currentUser.userRoles.userPatients.edit?
                                <ItemMenu actionWillRedirect={false} label={'Patient Options'} event={PAGES.PARTICIPANTS_DETAIL} eventParams={{patient_id: patient._id}}>
                                    <>
                                        <div className="_option" onClick={goToEditPatient}>
                                            <p>{t('edit-participant-capitalize')}</p>
                                        </div>
                                    </>
                                </ItemMenu> : ''
                        }
                    </div>
                    <div className="table">
                        <div>
                            <DemographicEntry label={t('users:dob')} value={getDateFormat(currentOrg, patient.dateOfBirth, 'short')} />
                            <DemographicEntry label={t('users:gender')} value={patient.user.bio.gender} capitalize={true} />
                            <DemographicEntry label={t('empi').toUpperCase()} value={patient.EMPI} />
                            <DemographicEntry label={t('mrn').toUpperCase()} value={patient.MRN} />
                        </div>
                        <div>
                            {
                                patient.phones.map((p, i) => (
                                    <DemographicEntry key={'graphic-'+i} label={p.label} value={getPhone(p)} />
                                ))
                            }
                            <DemographicEntry label={t('users:email')} value={patient.user.email} remark={true} />
                            <DemographicEntry label={t('users:address')} type="array" value={joinAddress(patient.address)} />
                        </div>
                    </div>
                </div>
                <div className="contacts card">
                    <div className="header">
                        <p className="title">
                            {t('users:contacts')}
                        </p>
                    </div>
                    <div className="table">
                        {
                            (patient.emergencyContacts || []).map((contact, index) => (
                                <ContactEntry key={'contact-'+index} contact={contact} />
                            ))
                        }
                    </div>
                </div>
                <div className="programs card">
                    <div className="header">
                        <p className="title">
                            {!!patient && patient.programId ? 1 : 0} {t('users:programs')}
                        </p>
                    </div>
                    {program ?
                        <div className="table">
                            <div className="programs-tabs">
                                <span>{program.name}</span>
                            </div>
                            <div className="programsList">
                                <div className="enrolled">
                                    {enrollStatusLabel()}
                                    <div className="enrolled-date">
                                        <p className="_section">{t('enrolled-at')}</p>
                                        <span>
                                            {patient.enrolledDate ? getDateFormat(currentOrg, patient.enrolledDate, 'long') : t('users:not-available')}
                                        </span>
                                    </div>
                                    <div className="enrolled-date">
                                        <p className="_section">{t('disenrollment')}</p>
                                        <span>
                                            {patient.expectedUnenrollment ? getDateFormat(currentOrg, patient.expectedUnenrollment, 'long') : t('no-disenrollment-set')}
                                        </span>
                                    </div>
                                    <Select required={false} disabled={!!currentUser && currentUser.userRoles ? !currentUser.userRoles.programsEnroll.edit : true}
                                            onChange={onStatusChangeRequested} placeholder={t('modify-enrollment')} options={programOptions}
                                            event={PAGES.PARTICIPANTS_DETAIL}/>
                                </div>
                                <div className="devices_label">{t('common:devices')}</div>
                                <div className="devices">
                                    {
                                        kits.map((kit, index) => (
                                            <KitEntry key={'entry-'+index} currentOrg={currentOrg} value={kit} />
                                        ))
                                    }
                                    {
                                        kits.length === 0 ? <div className="emptyMessage" >{t('common:no-monitoring-devices')}</div> : ''
                                    }
                                </div>
                                <div className="devices_label">Assigned to clinician(s)</div>
                                <div className="providers">
                                    {
                                        members.filter((u) => u.type === 'User').map((u, index) => (
                                            <>
                                                <div key={'user-'+index} className="providerItem">
                                                    <b>{u.name}</b>
                                                </div>
                                            </>
                                        ))
                                    }
                                </div>
                                <div className="devices_label">Assigned to team(s)</div>
                                <div className="providers">
                                    {
                                        members.filter((u) => u.type === 'Team').map((u, index) => (
                                            <>
                                                <div key={'team-'+index} className="providerItem">
                                                    <b>{u.name}</b>
                                                </div>
                                            </>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                        :
                        <div className="_containerBtn">
                            <span>
                                {t('participant-not-enrolled-program')}
                            </span>
                            {
                                currentUser.userRoles.userPatients.edit?
                                    <div className="_actionButton" onClick={onEnroll}>
                                        <button className="button icon " type="button">
                                            <span className="material-icons">add</span>
                                        </button>
                                        <p>{t('enroll')}</p>
                                    </div> : ''
                            }
                        </div>
                    }
                </div>
                <div className="timeline card" >
                    <div className="header">
                        <p className="title">
                            {t('timeline')}
                        </p>
                    </div>
                    <div className="table">

                        <div>
                            {
                                getTimeline().map((event, index) => (
                                    <EventEntry key={'event-'+index} currentOrg={currentOrg} event={event} />))
                            }
                        </div>
                    </div>
                </div>
            </div>
            {
                <ModalPatientExtent currentOrg={currentOrg} state={state} patient={patient} isOpen={extendEnrollmentModal} onStatusChange={onStatusChange} />
            }
            {
                <EnrollPatientDialog patient={patient} isOpen={openEnrollPatient} closeModal={onCloseModalEnroll} />
            }
        </div>
    )
}

export default PageViewLog(PatientDetail);
